import SEO from '../components/SEO';
import Schema, { createProductSchema } from '../components/Schema';
import React from 'react';
import { motion } from 'framer-motion';
import { ShoppingCart, Sparkles, Globe, Smartphone, Package, Users, Zap, CheckCircle2, BarChart3, Shield, Target, TrendingUp, Check } from 'lucide-react';
import ProductCTA from '../components/ProductCTA';

const FocusPointPage = () => {
  const stats = [
    { value: "Native", label: "SAP B1 Integration" },
    { value: "AI-Powered", label: "Intelligence" },
    { value: "Real-time", label: "Sync" },
    { value: "B2B & B2C", label: "Ready" }
  ];

  const whyChooseItems = [
    { 
      icon: Package, 
      title: "Built Exclusively for SAP B1", 
      strapline: "No middleware, no complexity",
      desc: "Native integration directly inside SAP Business One means seamless data flow, real-time sync, and zero integration headaches."
    },
    { 
      icon: Users, 
      title: "Implemented by SAP Experts", 
      strapline: "Certified consultants",
      desc: "Our certified SAP Business One consultants guide you from strategy to launch, ensuring your eCommerce delivers value from day one."
    },
    { 
      icon: Sparkles, 
      title: "AI-Powered Features", 
      strapline: "Intelligent automation",
      desc: "Smart personalisation, predictive ordering, and intelligent search powered by AI that understands your SAP data."
    },
    { 
      icon: Globe, 
      title: "Multi-Channel Distribution", 
      strapline: "Sell everywhere",
      desc: "Connect Amazon, marketplaces, and B2B portals with automated sync and unified inventory management."
    },
    { 
      icon: Smartphone, 
      title: "Mobile-First Design", 
      strapline: "Anywhere, anytime",
      desc: "Native mobile apps for B2B ordering, barcode scanning, and offline sync keep your team productive on the go."
    },
    { 
      icon: Shield, 
      title: "Advanced Modules", 
      strapline: "Specialised capabilities",
      desc: "Advanced quoting workflows, Product configurator, service calls, rental, and PIM-all connected to SAP Business One."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="FocusPoint eCommerce for SAP Business One | SAP-Certified Webstore"
        description="The only SAP-certified eCommerce platform built exclusively for SAP B1. B2B & B2C webstores with real-time inventory, AI features, marketplace integration."
        keywords="FocusPoint, SAP B1 ecommerce, SAP webstore, B2B ecommerce, SAP certified ecommerce"
        canonical="https://www.ascarii.com/products/focuspoint"
      />
      {/* Hero */}
      <section className="relative min-h-[650px] bg-gradient-to-br from-slate-900 via-blue-950 to-slate-950 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(59, 130, 246, 0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.2) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        </div>
        <div className="container mx-auto px-4 lg:px-8 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
              <div className="inline-block px-4 py-2 bg-blue-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full text-sm font-semibold mb-6">AI-Powered eCommerce for SAP B1</div>
              
              {/* FocusPoint Logo */}
              <div className="flex items-center gap-3 mb-6">
                <div className="w-14 h-14 rounded-xl bg-blue-500 flex items-center justify-center">
                  <ShoppingCart className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">FocusPoint</h1>
              </div>
              
              <p className="text-xl md:text-2xl text-blue-100 mb-4 font-medium">SAP experts, building eCommerce that works</p>
              <p className="text-lg text-gray-300 mb-8 leading-relaxed">Transform your SAP Business One into a powerful AI-driven eCommerce engine. Cut costs, boost sales, and keep your data in perfect sync with the only platform built exclusively for SAP B1.</p>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="relative">
              <img src="https://images.pexels.com/photos/6207729/pexels-photo-6207729.jpeg" alt="FocusPoint eCommerce" className="rounded-2xl shadow-2xl w-full h-[400px] object-cover" loading="eager" />
            </motion.div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0"><svg viewBox="0 0 1440 120" fill="none" className="w-full h-16"><path d="M0 0L60 10C120 20 240 40 360 46.7C480 53 600 47 720 43.3C840 40 960 40 1080 46.7C1200 53 1320 67 1380 73.3L1440 80V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V0Z" fill="white"/></svg></div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.1 }} viewport={{ once: true }} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">{stat.value}</div>
                <div className="text-sm md:text-base text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose FocusPoint - Dark Blue Background */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-950 to-slate-950">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(59, 130, 246, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Why SAP Business One Companies Choose FocusPoint</h2>
            <p className="text-xl text-blue-100 max-w-4xl mx-auto">Purpose-built for SAP B1 with AI intelligence and expert implementation</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChooseItems.map((item, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.05 }} viewport={{ once: true }} className="group p-8 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 hover:border-blue-400 hover:bg-white/20 transition-all">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-sm text-blue-300 font-medium mb-2">{item.strapline}</p>
                    <p className="text-blue-100 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FocusPoint AI */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="relative group flex">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl w-full">
                <img src="https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=85" alt="AI Technology" className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/20 to-transparent" />
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="flex flex-col justify-center">
              <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4 w-fit">FocusPoint AI</div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Smart AI for SAP Business One</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">AI capabilities designed specifically for SAP Business One operations-from predictive ordering to intelligent search that understands your catalogue.</p>
              <div className="space-y-3">
                {["Smart personalisation based on customer SAP history", "Intelligent search with natural language understanding", "Predictive ordering and demand forecasting", "Automated operations learning your business patterns"].map((item, i) => (
                  <div key={i} className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700 text-sm">{item}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Multi-Channel Distribution */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="flex flex-col justify-center">
              <div className="inline-block px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold mb-4 w-fit">Multi-Channel</div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Sell Everywhere from One Platform</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">Turn SAP Business One into your central sales hub with real-time marketplace sync and unified inventory management.</p>
              <div className="space-y-3">
                {["Connect Amazon and major marketplaces instantly", "Real-time inventory and pricing synchronisation", "Automated order flow into SAP Business One", "Unified dashboard for all sales channels"].map((item, i) => (
                  <div key={i} className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700 text-sm">{item}</p>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="relative group flex">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl w-full">
                <img src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=85" alt="Multi-Channel Sales" className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-tl from-purple-600/20 to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mobile App */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="relative group flex">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl w-full">
                <img src="https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800&q=85" alt="Mobile Commerce" className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/20 to-transparent" />
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="flex flex-col justify-center">
              <div className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-4 w-fit">Mobile App</div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">SAP Business One Goes Mobile</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">Full B2B ordering, barcode scanning, and offline sync for sales teams and customers on the go.</p>
              <div className="space-y-3">
                {["Complete B2B ordering with pricing and inventory", "Integrated barcode scanning for fast product selection", "Offline capability with automatic sync", "Real-time access to customer account data"].map((item, i) => (
                  <div key={i} className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700 text-sm">{item}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Advanced Modules */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="flex flex-col justify-center">
              <div className="inline-block px-4 py-2 bg-indigo-100 text-indigo-700 rounded-full text-sm font-semibold mb-4 w-fit">Advanced Modules</div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Powerful Customer Engagement Tools</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">Self-service portals, product configurators, and specialised modules for complex business needs-all seamlessly connected to SAP B1.</p>
              <div className="space-y-3">
                {["Self-Service Customer Portals", "Product Configurator for custom orders", "Service Call and PM Management", "Equipment Rental Management", "Product Information Management (PIM)", "Integrated with SAP Business One data"].map((item, i) => (
                  <div key={i} className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700 text-sm">{item}</p>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="relative group flex">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl w-full">
                <img src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=85" alt="Customer Portal" className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-tl from-indigo-600/20 to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-gray-100 via-blue-50 to-gray-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12 items-start">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="lg:col-span-2">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Transform Your eCommerce</h2>
              <p className="text-xl text-gray-600 mb-8">Built exclusively for SAP Business One</p>
              <div className="space-y-6">
                {[
                  { icon: Shield, title: "SAP Silver Partner", desc: "Certified expertise" },
                  { icon: Zap, title: "Fast Implementation", desc: "Expert guided setup" },
                  { icon: CheckCircle2, title: "Proven Platform", desc: "Hundreds of customers" }
                ].map((item, i) => (
                  <div key={i} className="flex items-center space-x-4">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/10 to-slate-500/10 flex items-center justify-center">
                      <item.icon className="w-7 h-7 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">{item.title}</h4>
                      <p className="text-gray-600 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="lg:col-span-3">
              <ProductCTA productName="FocusPoint" brandColor="blue-600" />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FocusPointPage;
